import { Box } from "@/components/ui/box";
import { Fab, FabIcon, FabLabel } from "@/components/ui/fab";
import { Text } from "@/components/ui/text";
import { FlatList } from "@/components/ui/flat-list";
import { Heading } from "@/components/ui/heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import { HStack } from "@/components/ui/hstack";
import { Pressable } from "@/components/ui/pressable";
import {
  getOrganizationControllerListQueryKey,
  useOrganizationControllerCreate,
  useOrganizationControllerList,
  useUserControllerRequestManagerAccess,
} from "@/generated/api/api";
import { OrganizationDTO } from "@/generated/api/models";
import { EmptyState } from "@/screens/common/empty.state";
import { RestaurantListItem } from "@/screens/restaurants/restaurant.list.item";
import { useSession } from "@/utils/session.ctx";
import { Link } from "expo-router";
import React, { ComponentPropsWithoutRef, useState } from "react";
import { Input, InputField } from "@/components/ui/input";
import { JoinByCodeModal } from "@/screens/invitation/join.code.modal";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, Store, KeyRound } from "lucide-react-native";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import { useToast } from "@/utils/useToast";
import { create } from "zustand";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { z } from "zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Keyboard } from "react-native";
import { ChevronDownIcon, CloseIcon, Icon } from "@/components/ui/icon";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/ui/select";

const useRequestManagerStore = create<{
  isRequested: boolean;
  setIsRequested: (isRequested: boolean) => void;
}>((set) => ({
  isRequested: false,
  setIsRequested: (isRequested) => set(() => ({ isRequested })),
}));

function ListItem(props: OrganizationDTO) {
  return (
    <Link
      href={{
        pathname: "/(tabs)/orgs/[orgId]",
        params: { orgId: props.id },
      }}
      className="w-full"
    >
      <RestaurantListItem {...props} />
    </Link>
  );
}

function RequestAccessModal(props: ComponentPropsWithoutRef<typeof Modal>) {
  const { mutate: requestManagerAccess, isPending: isRequestingManagerAccess } =
    useUserControllerRequestManagerAccess();

  const [showAccessRequested, setShowAccessRequested] = useState(false);
  const { setIsRequested } = useRequestManagerStore();

  const { showToast } = useToast();

  return (
    <Modal {...props}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-0.5">
          <Heading>Request manager access</Heading>
        </ModalHeader>
        <ModalBody className="mb-4">
          <Text>
            If you're a business owner or manager, you can request access to add
            and manage tips for business on our platform. Once submitted, your
            request will be reviewed and access will be granted within two
            business days.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            action="secondary"
            onPress={props.onClose}
            size="sm"
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            size="sm"
            isDisabled={isRequestingManagerAccess}
            onPress={() => {
              requestManagerAccess(undefined, {
                onSuccess() {
                  setIsRequested(true);
                  showToast(
                    "success",
                    "Access requested. We will revert to you within two business days"
                  );
                  props.onClose();
                },
              });
            }}
          >
            <ButtonText>Request Access</ButtonText>
            {isRequestingManagerAccess && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function RestaurantList() {
  const {
    data: listData,
    isRefetching,
    refetch,
    isError: isListError,
  } = useOrganizationControllerList();

  // const listData: OrganizationDTO[] = [];

  const [showModal, setShowModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  // Always enabled due to required review of organization after creation
  const [hasCreatePermission] = useState(true);
  // const claims = useAccessTokenClaims();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const { isRequested: isManagerAccessRequested } = useRequestManagerStore();

  // useEffect(() => {
  //   setHasCreatePermission(
  //     claims?.externalClaims?.whitelisted === true ||
  //       claims?.roles?.includes("organization_manager") === true ||
  //       claims?.scope?.includes("create:organization") === true
  //   );
  // }, [claims]);

  return (
    <Box className="w-full h-full">
      <FlatList
        className="h-full w-full flex-1 md:mb-2"
        contentContainerClassName="p-4 pb-4 grow gap-4"
        data={listData}
        keyExtractor={(info) => info.id}
        renderItem={(info) => {
          return (
            <Box className="w-full">
              <ListItem {...info.item} />
            </Box>
          );
        }}
        refreshing={isRefetching}
        onRefresh={refetch}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <VStack className="h-full w-full p-3 justify-center">
            {listData?.length === 0 ? (
              <VStack className="gap-6 items-center">
                <Heading size="xl">Ready to receive tips?</Heading>
                <HStack className="gap-3">
                  <Pressable
                    onPress={() => setShowModal(true)}
                    className="flex-1 border border-outline-200 rounded-md p-4 items-center gap-3"
                  >
                    <Icon as={Store} size="xl" className="text-primary-500" />
                    <VStack className="items-center gap-1">
                      <Text bold>Add business</Text>
                      <Text size="xs" className="text-typography-500 text-center">
                        Start receiving tips
                      </Text>
                    </VStack>
                  </Pressable>
                  <Pressable
                    onPress={() => setShowJoinModal(true)}
                    className="flex-1 border border-outline-200 rounded-md p-4 items-center gap-3"
                  >
                    <Icon as={KeyRound} size="xl" className="text-primary-500" />
                    <VStack className="items-center gap-1">
                      <Text bold>Join team</Text>
                      <Text size="xs" className="text-typography-500 text-center">
                        Got a code? Join existing team
                      </Text>
                    </VStack>
                  </Pressable>
                </HStack>
              </VStack>
            ) : isListError ? (
              <EmptyState
                title="Something went wrong"
                description="Please check your internet connection and try again"
              />
            ) : (
              <Spinner size="large" />
            )}
          </VStack>
        )}
      />
      {hasCreatePermission && (
        <Menu
          placement="top right"
          offset={8}
          trigger={({ ...triggerProps }) => (
            <Fab
              size="md"
              placement="bottom right"
              isHovered={false}
              isDisabled={false}
              isPressed={false}
              {...triggerProps}
            >
              <FabIcon as={Plus} />
              <FabLabel>Add / Join</FabLabel>
            </Fab>
          )}
        >
          <MenuItem key="add" onPress={() => setShowModal(true)}>
            <Icon as={Store} size="sm" className="mr-2 text-typography-700" />
            <MenuItemLabel>Add business</MenuItemLabel>
          </MenuItem>
          <MenuItem key="join" onPress={() => setShowJoinModal(true)}>
            <Icon as={KeyRound} size="sm" className="mr-2 text-typography-700" />
            <MenuItemLabel>Join with code</MenuItemLabel>
          </MenuItem>
        </Menu>
      )}
      <RestaurantCreateModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
      <JoinByCodeModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
      />
      <RequestAccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
      />
    </Box>
  );
}

const BUSINESS_TYPE_KEYS = [
  "RESTAURANTS_CAFES",
  "BARS_LOUNGES",
  "STREET_FOOD_TAKEAWAY",
  "HOTELS_GUEST_STAYS",
  "EVENTS_CATERING",
  "HAIR_GROOMING",
  "DELIVERY_DRIVING",
] as const;

const BUSINESS_TYPES: {
  [k in (typeof BUSINESS_TYPE_KEYS)[number]]: {
    title: string;
    description: string;
  };
} = {
  RESTAURANTS_CAFES: {
    title: "Restaurants & Cafes",
    description: "Restaurants, Cafés, Diners, Bistros, Coffee Shops",
  },
  BARS_LOUNGES: {
    title: "Bars & Lounges",
    description: "Bars, Pubs, Wine Bars, Lounges",
  },
  STREET_FOOD_TAKEAWAY: {
    title: "Street Food & Takeaway",
    description: "Food Trucks, Food Stalls, Street Markets, Pop-ups",
  },
  HOTELS_GUEST_STAYS: {
    title: "Hotels & Guest Stays",
    description: "Hotels, Resorts, Inns, Hostels, Guesthouses",
  },
  EVENTS_CATERING: {
    title: "Events & Catering",
    description: "Event Venues, Banquet Halls, Outdoor Events, Private Parties",
  },
  HAIR_GROOMING: {
    title: "Hair & Grooming",
    description: "Barber Shops, Hair Salons, Men’s Grooming Studios",
  },
  DELIVERY_DRIVING: {
    title: "Delivery & Driving",
    description:
      "Delivery Services, Ride-share, Catering Drivers, Shuttle Services",
  },
} as const;

const RestaurantSchema = z
  .object({
    name: z.string().min(1, "Business name is required"),
    currency: z.enum(["EUR/2"]),
    type: z.enum([...BUSINESS_TYPE_KEYS, "OTHER"], {
      message: "Business type is required",
    }),
    description: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === "OTHER" && !data.description) {
      ctx.addIssue({
        path: ["description"],
        code: z.ZodIssueCode.too_small,
        minimum: 5,
        type: "string",
        inclusive: true,
        message: "Business description is required",
      });
    }
  });

type RestaurantSchemaType = z.infer<typeof RestaurantSchema>;

export function RestaurantCreateModal(
  props: Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RestaurantSchemaType>({
    resolver: zodResolver(RestaurantSchema),
    defaultValues: {
      currency: "EUR/2",
    },
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const typeValue = useWatch({ control, name: "type" });

  const { mutateAsync: createOrg } = useOrganizationControllerCreate({
    mutation: {
      onSuccess: (orgDto) => {
        showToast("success", "Business has been successfully added");

        queryClient.setQueryData(
          getOrganizationControllerListQueryKey(),
          (old: OrganizationDTO[] | undefined) => {
            const result = [...(old ?? []), orgDto];

            return result;
          }
        );
        props.onClose?.();
        reset();
      },
      mutationKey: ['create_org']
    },
  });

  const onSubmit = (data: RestaurantSchemaType) => {
    return createOrg({
      data,
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Add business</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl isInvalid={!!errors?.name} isRequired className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Business name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="name"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Enter name"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.name?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl
            isInvalid={!!errors?.currency}
            isRequired
            className="w-full"
          >
            <FormControlLabel>
              <FormControlLabelText>Tips Currency</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue="EUR/2"
              name="currency"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input isDisabled>
                  <InputField
                    placeholder="Currency of incoming tips"
                    value={value === "EUR/2" ? "€ (EUR)" : value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.currency?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors?.type} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Business Type</FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="type"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select onValueChange={onChange} selectedValue={value}>
                  <SelectTrigger>
                    <SelectInput
                      placeholder="e.g. Restaurant, Cafe, Barber Shop"
                      className="flex-1"
                      onBlur={onBlur}
                    />
                    <SelectIcon className="mr-3" as={ChevronDownIcon} />
                  </SelectTrigger>
                  <SelectPortal>
                    <SelectBackdrop />
                    <SelectContent>
                      <SelectDragIndicatorWrapper>
                        <SelectDragIndicator />
                      </SelectDragIndicatorWrapper>
                      {Object.entries(BUSINESS_TYPES).map(([type, info]) => (
                        <SelectItem
                          key={type}
                          label={info.title}
                          value={type}
                        />
                      ))}
                      <SelectItem key="OTHER" label="Other..." value="OTHER" />
                    </SelectContent>
                  </SelectPortal>
                </Select>
              )}
            />
            {!!typeValue && typeValue !== "OTHER" && (
              <FormControlHelper>
                <FormControlHelperText>
                  {BUSINESS_TYPES[typeValue].description}
                </FormControlHelperText>
              </FormControlHelper>
            )}
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.type?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          {typeValue === "OTHER" && (
            <FormControl
              isInvalid={!!errors?.description}
              isRequired
              className="w-full"
            >
              <FormControlLabel>
                <FormControlLabelText>
                  Business description
                </FormControlLabelText>
              </FormControlLabel>
              <Controller
                defaultValue=""
                name="description"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input>
                    <InputField
                      placeholder="Short business description"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      onSubmitEditing={handleKeyPress}
                      returnKeyType="done"
                    />
                  </Input>
                )}
              />
              <FormControlHelper>
                <FormControlHelperText>
                  Please provide brief description of business, what for are you
                  receiving tips?
                </FormControlHelperText>
              </FormControlHelper>
              <FormControlError>
                <FormControlErrorIcon as={AlertTriangle} />
                <FormControlErrorText>
                  {errors?.description?.message}
                </FormControlErrorText>
              </FormControlError>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Create</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
